import {
    Box,
    buildPath,
    buildTemplatePath,
    Card,
    Grid,
    Page,
    PageRoute,
    Stack,
    Typography,
} from "@compass/core";
import {
    DndContext,
    closestCenter,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from "@dnd-kit/core";
import {
    SortableContext,
    arrayMove,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Home, LkRoleId } from "generated/openApi";
import { useEffect, useMemo, useState, useCallback } from "react";
import { useAppSelector } from "hooks";
import ApiService from "services/apiService";
import { DashboardGrid, SortableCard } from "./components/DashboardGrid";
import { DashboardDataProviders } from "./components/DashboardDataProviders";
import { ManageButton } from "./components/ManageButton";
import { DashboardCardContext, DashboardCardContextValue } from "./components/DashboardCard";
import { CardConfigurationDrawer, DashboardTabBar} from "./components";
import { TabEmptyState } from "./components/TabEmptyState";
import { DashboardCardConfig, DashboardTabState, CardId, TabColorKey } from "./types";
import { DashboardConfigService } from "./services";
import { DASHBOARD_CARD_REGISTRY } from "./DashboardCardRegistry";
import { ExpandedCardDialog } from "./components/ExpandedCardDialog";
import { KPI_DEMO_DATA_ACTIVE, KpiAudience, KpiStrip, useKpiData } from "./KpiCards";


const routeName = "home";

export const homePageRoute: PageRoute = {
    handle: "Home",
    templatePath: buildTemplatePath(routeName),
    getPath: () => buildPath(routeName),
};

interface CardContextWrapperProps {
    card: DashboardCardConfig;
    onToggle: (cardId: CardId) => void;
    onExpand: (cardId: CardId) => void;
    children: React.ReactNode;
}

const CardContextWrapper = ({ card, onToggle, onExpand, children }: CardContextWrapperProps) => {
    const value = useMemo<DashboardCardContextValue>(() => ({
        onRemove: () => onToggle(card.id),
        onExpand: () => onExpand(card.id),
        isExpanded: false,
    }), [card.id, onToggle, onExpand]);

    return (
        <DashboardCardContext.Provider value={value}>
            {children}
        </DashboardCardContext.Provider>
    );
};

export const HomePage = () => {
    const reactMenus = useAppSelector((state) => state.app.reactMenus);
    const appRoleId = useAppSelector((state) => state.app.roleId);

    const [home, setHome] = useState<Home | null>(null);
    const [tabState, setTabState] = useState<DashboardTabState | null>(null);
    const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
    const [expandedCardId, setExpandedCardId] = useState<CardId | null>(null);
    const [kpiAudience, setKpiAudience] = useState<KpiAudience>("student");
    const { items: kpiItems, loading: kpiLoading, error: kpiError } = useKpiData(kpiAudience);
    const activeTabId = tabState?.activeTabId ?? '';
    const tabs = tabState?.tabs ?? [];

    const visibleCards = useMemo(
        () => tabState ? DashboardConfigService.getVisibleCards(tabState, activeTabId, reactMenus) : [],
        [tabState, activeTabId, reactMenus]
    );

    const authorizedCards = useMemo(
        () => tabState
            ? DashboardConfigService.getAllCardsForTab(tabState, activeTabId, reactMenus)
            : [],
        [tabState, activeTabId, reactMenus]
    );

    const isAtCardLimit = useMemo(
        () => tabState ? DashboardConfigService.isTabAtCardLimit(tabState, activeTabId) : false,
        [tabState, activeTabId]
    );

    const hasVisibleCards = visibleCards.length > 0;

    const sensors = useSensors(useSensor(PointerSensor));

    useEffect(() => {
        ApiService.home.get().then(setHome);
    }, []);

    useEffect(() => {
        const cardDefs = home?.dashboardCards;
        if (!cardDefs) return;
        ApiService.dashboardTab.getAll().then((tabs) => {
            setTabState(DashboardConfigService.setupDashboardTabs(tabs, cardDefs));
        });
    }, [home]);


    // ─── Drawer Helpers

    const handleManageClick = useCallback(() => setIsDrawerOpen(true), []);
    const handleDrawerClose = useCallback(() => setIsDrawerOpen(false), []);
    const handleEmptyStateAddCards = useCallback(() => setIsDrawerOpen(true), []);

    // ─── Tab Handlers

    const handleSelectTab = useCallback((tabId: string) => {
        setTabState((prev) => {
            if (!prev) return prev;
            return DashboardConfigService.setActiveTab(prev, tabId);
        });
    }, []);

    const handleAddTab = useCallback(async (label: string) => {
        if (!tabState) return;
        try {
            const updated = await DashboardConfigService.createTab(tabState, label);
            setTabState(updated);
        } catch (error) {
            console.error('Failed to create tab:', error);
        }
    }, [tabState]);

    const handleTabBarAddTab = useCallback(() => {
        handleAddTab(`Tab ${(tabState?.tabs.length ?? 0) + 1}`);
    }, [handleAddTab, tabState?.tabs.length]);

    const handleRenameTab = useCallback((tabId: string, newLabel: string) => {
        setTabState((prev) => {
            if (!prev) return prev;
            return DashboardConfigService.renameTab(prev, tabId, newLabel);
        });
    }, []);

    const handleSetTabColor = (tabId: string, colorKey: TabColorKey) => {
        if (!tabState) return;
        const updated = DashboardConfigService.setTabColor(tabState, tabId, colorKey);
        setTabState(updated);
    };

    const handleDeleteTab = useCallback(async (tabId: string) => {
        if (!tabState) return;
        try {
            const updated = await DashboardConfigService.deleteTab(tabState, tabId);
            setTabState(updated);
        } catch (error) {
            console.error('Failed to delete tab:', error);
        }
    }, [tabState]);

    // ─── Drag-and-Drop (scoped to active tab)

    const handleDragEnd = useCallback((event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        setTabState((prev) => {
            if (!prev) return prev;

            const currentActiveTabId = prev.activeTabId;
            const visible = DashboardConfigService.getVisibleCards(prev, currentActiveTabId, reactMenus);
            const visibleIds = visible.map((c) => c.id);

            const oldIndex = visibleIds.indexOf(Number(active.id) as CardId);
            const newIndex = visibleIds.indexOf(Number(over.id) as CardId);
            if (oldIndex === -1 || newIndex === -1) return prev;

            const reorderedVisibleIds = arrayMove(visibleIds, oldIndex, newIndex);

            const tab = prev.tabs.find((t) => t.id === currentActiveTabId);
            if (!tab) return prev;

            const visibleIdSet = new Set(reorderedVisibleIds);
            const reorderedVisible = reorderedVisibleIds
                .map((id) => tab.cards.find((card) => card.id === id))
                .filter((card): card is DashboardCardConfig => !!card);

            const nonVisible = tab.cards
                .filter((card) => !visibleIdSet.has(card.id))
                .sort((a, b) => a.displayOrder - b.displayOrder);

            const reorderedCards = [...reorderedVisible, ...nonVisible].map(
                (card, index) => ({ ...card, displayOrder: index + 1 })
            );

            return DashboardConfigService.reorderCards(prev, currentActiveTabId, reorderedCards);
        });
    }, [reactMenus]);

    // ─── Card Handlers 

    const handleToggleCard = useCallback((cardId: CardId) => {
        setTabState((prev) => {
            if (!prev) return prev;
            return DashboardConfigService.toggleCard(prev, prev.activeTabId, cardId);
        });
    }, []);

    const handleExpandCard = useCallback((cardId: CardId) => {
        setExpandedCardId(cardId);
    }, []);

    const handleCloseExpanded = useCallback(() => {
        setExpandedCardId(null);
    }, []);

    return (
        <Page>
            <Stack direction="column" spacing="lg" sx={{ width: '100%' }}>
                <Card>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Stack direction="column" spacing="sm">
                            <Typography variant="h4">
                                {home ? `Welcome, ${home.userFirstName}` : 'Welcome'}
                            </Typography>
                        </Stack>
                        <Stack direction="row" spacing="md" alignItems="center">
                            <ManageButton onClick={handleManageClick} />
                            <Box>
                            </Box>
                        </Stack>
                    </Stack>
                </Card>

                {/* Tab Bar — horizontally scrollable*/}
                <Box
                    sx={{
                        overflowX: 'auto',
                        overflowY: 'hidden',
                        px: 0.5,
                        scrollbarWidth: 'none',          // Firefox
                        '&::-webkit-scrollbar': {         // Chrome, Safari, Edge
                            display: 'none',
                        },
                    }}
                >
                    {appRoleId === LkRoleId.Cobro && (
                        <KpiStrip
                            items={kpiItems}
                            loading={kpiLoading}
                            error={kpiError}
                            showAudienceToggle
                            sampleDataNotice={KPI_DEMO_DATA_ACTIVE}
                            audience={kpiAudience}
                            onAudienceChange={setKpiAudience}
                        />
                    )}
                    <DashboardTabBar
                        tabs={tabs}
                        activeTabId={activeTabId}
                        onSelectTab={handleSelectTab}
                        onAddTab={handleTabBarAddTab}
                    />
                </Box>

                <Box sx={{ width: "100%" }}>
                    {hasVisibleCards ? (
                        <DashboardDataProviders visibleCardIds={visibleCards.map(c => c.id)}>
                            <DndContext
                                sensors={sensors}
                                collisionDetection={closestCenter}
                                onDragEnd={handleDragEnd}
                            >
                                <SortableContext
                                    items={visibleCards.map((c) => c.id)}
                                    strategy={verticalListSortingStrategy}
                                >
                                    <DashboardGrid>
                                        {visibleCards.map((card) => {
                                            const CardComponent = DASHBOARD_CARD_REGISTRY[card.id];

                                            if (!CardComponent) {
                                                console.warn(`Card component not found in registry for code: ${card.id}`);
                                                return null;
                                            }

                                            return (
                                                <Grid
                                                    key={`${activeTabId}-${card.id}`}
                                                    item
                                                    xs={12} md={4}
                                                    sx={{ display: "flex" }}
                                                >
                                                    <SortableCard id={card.id}>
                                                        <CardContextWrapper
                                                            card={card}
                                                            onToggle={handleToggleCard}
                                                            onExpand={handleExpandCard}
                                                        >
                                                            <CardComponent />
                                                        </CardContextWrapper>
                                                    </SortableCard>
                                                </Grid>
                                            );
                                        })}
                                    </DashboardGrid>
                                </SortableContext>
                            </DndContext>

                            <ExpandedCardDialog
                                cardId={expandedCardId}
                                cardConfigs={visibleCards}
                                onClose={handleCloseExpanded}
                            />
                        </DashboardDataProviders>
                    ) : (
                        <TabEmptyState onAddCardsClick={handleEmptyStateAddCards} />
                    )}
                </Box>
            </Stack>

            <CardConfigurationDrawer
                open={isDrawerOpen}
                onClose={handleDrawerClose}
                tabs={tabs}
                activeTabId={activeTabId}
                isAtCardLimit={isAtCardLimit}
                cards={authorizedCards}
                onToggleCard={handleToggleCard}
                onAddTab={handleAddTab}
                onRenameTab={handleRenameTab}
                onDeleteTab={handleDeleteTab}
                onSetTabColor={handleSetTabColor}
            />
        </Page>
    );
};
